import React, { useState, useEffect, useCallback } from 'react';
import { Upload, Send, Mail, Eye, Trash2, Pencil, X, RefreshCw, FileText } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLE = {
  pending: 'bg-slate-100 text-slate-600',
  queued: 'bg-amber-50 text-amber-700',
  sent: 'bg-green-50 text-green-700',
  failed: 'bg-rose-50 text-rose-600',
};

export const CertDeliveryView = ({ API_URL, getAuthHeader }) => {
  const [data, setData] = useState({ certificates: [], summary: {} });
  const [tpl, setTpl] = useState(null);
  const [showTpl, setShowTpl] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editCert, setEditCert] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [cr, tr] = await Promise.all([
        fetch(`${API_URL}/api/uploaded-certs`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/uploaded-certs/email-template`, { headers: getAuthHeader() }),
      ]);
      if (cr.ok) setData(await cr.json());
      if (tr.ok) setTpl(await tr.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // poll while anything is queued
  useEffect(() => {
    if (!data.certificates.some(c => c.status === 'queued')) return;
    const t = setInterval(fetchAll, 3000);
    return () => clearInterval(t);
  }, [data, fetchAll]);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const fd = new FormData();
    [...files].forEach(f => fd.append('files', f));
    try {
      const r = await fetch(`${API_URL}/api/uploaded-certs/upload`, { method: 'POST', headers: getAuthHeader(), body: fd });
      const d = await r.json();
      if (r.ok) {
        const matched = d.results.filter(x => x.status === 'matched').length;
        const needs = d.results.filter(x => x.status === 'needs_email').length;
        toast.success(`${d.results.length} file(s) processed — ${matched} matched to registrants${needs ? `, ${needs} need an email` : ''}`);
        fetchAll();
      } else toast.error(d.detail || 'Upload failed');
    } catch { toast.error('Upload failed'); }
    setBusy(false);
  };

  const saveTemplate = async () => {
    const r = await fetch(`${API_URL}/api/uploaded-certs/email-template`, {
      method: 'PATCH', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ subject: tpl.subject, body: tpl.body })
    });
    if (r.ok) { toast.success('Email template saved'); setShowTpl(false); setTpl(await r.json()); }
    else toast.error('Could not save template');
  };

  const openPreview = async (c) => {
    const r = await fetch(`${API_URL}/api/uploaded-certs/${c.id}/preview`, { headers: getAuthHeader() });
    if (r.ok) setPreview({ cert: c, ...(await r.json()) });
  };

  const saveEdit = async (andResend = false) => {
    const r = await fetch(`${API_URL}/api/uploaded-certs/${editCert.id}`, {
      method: 'PATCH', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: editCert.email || '', recipient_name: editCert.recipient_name || '' })
    });
    if (!r.ok) { const d = await r.json().catch(() => ({})); return toast.error(d.detail || 'Update failed'); }
    const saved = await r.json();
    if (andResend && saved.email) {
      const sr = await fetch(`${API_URL}/api/uploaded-certs/${saved.id}/send`, { method: 'POST', headers: getAuthHeader() });
      if (sr.ok) toast.success(`Fixed email — resending to ${saved.email}`);
      else { const d = await sr.json().catch(() => ({})); toast.error(d.detail || 'Saved, but resend failed'); }
    } else {
      toast.success('Recipient updated');
    }
    setEditCert(null);
    fetchAll();
  };

  const sendOne = async (c) => {
    if (!window.confirm(`Send the certificate to ${c.recipient_name} (${c.email})?`)) return;
    const r = await fetch(`${API_URL}/api/uploaded-certs/${c.id}/send`, { method: 'POST', headers: getAuthHeader() });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { toast.success('Sending…'); fetchAll(); }
    else toast.error(d.detail || 'Send failed');
  };

  const sendAll = async () => {
    const n = data.summary.ready + (data.summary.failed || 0);
    if (!window.confirm(`Send certificates to all ${n} ready recipient(s)? Each gets their own email with their certificate attached.`)) return;
    const r = await fetch(`${API_URL}/api/uploaded-certs/send-all`, { method: 'POST', headers: getAuthHeader() });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { toast.success(`${d.queued} email(s) queued — sending in the background`); fetchAll(); }
    else toast.error(d.detail || 'Nothing ready to send');
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove "${c.filename}" from the delivery list?`)) return;
    const r = await fetch(`${API_URL}/api/uploaded-certs/${c.id}`, { method: 'DELETE', headers: getAuthHeader() });
    if (r.ok) { toast.success('Removed'); fetchAll(); }
  };

  const viewPdf = async (c) => {
    const r = await fetch(`${API_URL}/api/uploaded-certs/${c.id}/file`, { headers: getAuthHeader() });
    if (!r.ok) return toast.error('Could not load the PDF');
    const blob = await r.blob();
    window.open(URL.createObjectURL(blob), '_blank');
  };

  const s = data.summary;
  return (
    <div className="space-y-4" data-testid="cert-delivery-view">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex flex-wrap items-center gap-3">
          <label className={`inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-lg cursor-pointer ${busy ? 'opacity-60 pointer-events-none' : ''}`}>
            <Upload className="w-4 h-4" /> {busy ? 'Uploading…' : 'Upload certificate PDFs'}
            <input type="file" accept=".pdf" multiple className="hidden" data-testid="cert-delivery-upload-input"
              onChange={e => { upload(e.target.files); e.target.value = ''; }} />
          </label>
          <button onClick={() => setShowTpl(t => !t)} data-testid="cert-delivery-template-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-600 text-sm font-semibold rounded-lg hover:bg-slate-50">
            <Mail className="w-4 h-4" /> Edit email letter
          </button>
          <button onClick={async () => {
            const r = await fetch(`${API_URL}/api/uploaded-certs/rematch`, { method: 'POST', headers: getAuthHeader() });
            const d = await r.json().catch(() => ({}));
            if (r.ok) { toast.success(d.rematched ? `${d.rematched} certificate(s) re-matched to registrations` : 'No new matches found'); fetchAll(); }
          }} data-testid="cert-delivery-rematch-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-600 text-sm font-semibold rounded-lg hover:bg-slate-50">
            <RefreshCw className="w-4 h-4" /> Re-match registrations
          </button>
          {s.sent > 0 && (
            <button onClick={async () => {
              toast.info('Checking delivery receipts…');
              const r = await fetch(`${API_URL}/api/uploaded-certs/refresh-receipts`, { method: 'POST', headers: getAuthHeader() });
              const d = await r.json().catch(() => ({}));
              if (r.ok) { toast.success(`${d.checked} receipt(s) checked — ${d.updated} updated`); fetchAll(); }
            }} data-testid="cert-delivery-receipts-btn"
              className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-600 text-sm font-semibold rounded-lg hover:bg-slate-50">
              <Eye className="w-4 h-4" /> Check receipts
            </button>
          )}
          <button onClick={sendAll} disabled={!s.ready && !s.failed} data-testid="cert-delivery-send-all"
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold rounded-lg disabled:opacity-40 ml-auto">
            <Send className="w-4 h-4" /> Send all ready ({(s.ready || 0) + (s.failed || 0)})
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mt-4 text-xs">
          <span className="px-3 py-1.5 rounded-lg bg-slate-100 font-semibold text-slate-600" data-testid="cert-delivery-total">{s.total || 0} certificate(s) uploaded</span>
          <span className="px-3 py-1.5 rounded-lg bg-teal-50 font-semibold text-teal-700">{s.ready || 0} ready to send</span>
          <span className={`px-3 py-1.5 rounded-lg font-semibold ${s.needs_email ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-400'}`} data-testid="cert-delivery-needs-email">{s.needs_email || 0} need an email address</span>
          <span className="px-3 py-1.5 rounded-lg bg-green-50 font-semibold text-green-700">{s.sent || 0} sent</span>
          {s.failed > 0 && <span className="px-3 py-1.5 rounded-lg bg-rose-50 font-semibold text-rose-600">{s.failed} failed — edit & resend</span>}
          {s.bounced > 0 && <span className="px-3 py-1.5 rounded-lg bg-amber-100 font-bold text-amber-800" data-testid="cert-delivery-bounced-chip">{s.bounced} bounced — fix email & resend</span>}
        </div>
        <p className="text-[11px] text-slate-400 mt-3">Each PDF is matched to a Play Science Institute registrant by the name in its filename. Unmatched certificates stay here until you add an email manually. Emails are sent from admin@dcelc.org with the certificate attached.</p>
      </div>

      {showTpl && tpl && (
        <div className="bg-white rounded-xl border-2 border-teal-200 p-5 space-y-3" data-testid="cert-delivery-template-form">
          <h4 className="font-bold text-slate-800">Email letter — use {'{{name}}'} where the participant's name should appear</h4>
          <input value={tpl.subject} onChange={e => setTpl(t => ({ ...t, subject: e.target.value }))}
            data-testid="cert-delivery-template-subject" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-semibold" />
          <textarea value={tpl.body} rows={10} onChange={e => setTpl(t => ({ ...t, body: e.target.value }))}
            data-testid="cert-delivery-template-body" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm leading-relaxed" />
          <div className="flex gap-2">
            <button onClick={saveTemplate} data-testid="cert-delivery-template-save" className="px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg">Save letter</button>
            <button onClick={() => setShowTpl(false)} className="px-4 py-2 border border-slate-200 text-slate-500 text-sm font-semibold rounded-lg">Cancel</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="cert-delivery-table">
          <thead><tr className="text-left text-xs uppercase text-slate-500 border-b bg-slate-50">
            <th className="py-3 px-4">Recipient</th><th>Email</th><th>Match</th><th>File</th><th>Status</th><th className="px-2">Actions</th>
          </tr></thead>
          <tbody>
            {data.certificates.length === 0 && (
              <tr><td colSpan={6} className="py-12 text-center text-slate-400">
                No certificates uploaded yet. Upload the PDFs above — names are matched to Play Science registrations automatically.
              </td></tr>
            )}
            {data.certificates.map(c => {
              const bounced = ['bounced', 'complained'].includes(c.delivery_status);
              return (
              <tr key={c.id} className={`border-b border-slate-100 ${bounced ? 'bg-amber-50 hover:bg-amber-100/70' : 'hover:bg-slate-50'}`} data-testid={`cert-delivery-row-${c.id}`}>
                <td className="py-3 px-4 font-medium text-slate-800">{c.recipient_name}</td>
                <td className="text-xs">{c.email || <span className="text-amber-600 font-semibold">Needs email</span>}</td>
                <td className="text-[11px]">
                  {c.matched ? <span className="text-green-700 font-semibold">Registered{c.match_confidence < 1 ? ` (~${Math.round(c.match_confidence * 100)}%)` : ''}</span>
                    : <span className="text-slate-400">Not registered{c.manually_edited ? ' · manual' : ''}</span>}
                </td>
                <td className="text-[11px] text-slate-400 max-w-[180px] truncate" title={c.filename}>{c.filename}</td>
                <td>
                  <span className={`text-[10px] font-black uppercase px-2 py-1 rounded ${STATUS_STYLE[c.status] || ''}`} title={c.error || ''}>
                    {c.status}{c.sent_at ? ` ${String(c.sent_at).slice(0, 10)}` : ''}
                  </span>
                  {c.status === 'sent' && c.delivery_status && c.delivery_status !== 'sent' && (
                    <span className={`ml-1 text-[10px] font-bold px-2 py-1 rounded ${['delivered', 'opened', 'clicked'].includes(c.delivery_status) ? 'bg-green-100 text-green-800' : ['bounced', 'complained', 'delivery_delayed'].includes(c.delivery_status) ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-500'}`}
                      title={c.delivery_checked_at ? `Checked ${String(c.delivery_checked_at).slice(0, 16).replace('T', ' ')} UTC` : ''}
                      data-testid={`cert-delivery-receipt-${c.id}`}>
                      {c.delivery_status === 'delivered' ? '✓ delivered' : c.delivery_status === 'opened' ? '✓✓ opened' : c.delivery_status.replace('_', ' ')}
                    </span>
                  )}
                </td>
                <td className="px-2">
                  <span className="flex gap-0.5 items-center">
                    {bounced && (
                      <button onClick={() => setEditCert({ ...c, _resend: true })} data-testid={`cert-delivery-fix-${c.id}`}
                        className="px-2 py-1 rounded-lg text-[10px] font-black uppercase bg-amber-500 text-white hover:bg-amber-600 mr-1">
                        Fix &amp; resend
                      </button>
                    )}
                    <button onClick={() => viewPdf(c)} title="View certificate PDF" className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"><FileText className="w-3.5 h-3.5" /></button>
                    <button onClick={() => openPreview(c)} title="Preview email letter" data-testid={`cert-delivery-preview-${c.id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-teal-600 hover:bg-teal-50"><Eye className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setEditCert({ ...c })} title="Edit recipient / email" data-testid={`cert-delivery-edit-${c.id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"><Pencil className="w-3.5 h-3.5" /></button>
                    {c.status !== 'sent' && c.status !== 'queued' && (
                      <button onClick={() => sendOne(c)} disabled={!c.email} title={c.email ? 'Send now' : 'Add an email first'}
                        data-testid={`cert-delivery-send-${c.id}`}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-green-600 hover:bg-green-50 disabled:opacity-30"><Send className="w-3.5 h-3.5" /></button>
                    )}
                    {(c.status === 'sent' || c.status === 'queued') && (
                      <button onClick={async () => { await fetch(`${API_URL}/api/uploaded-certs/${c.id}/reset`, { method: 'POST', headers: getAuthHeader() }); fetchAll(); }}
                        title="Reset to allow re-send" data-testid={`cert-delivery-reset-${c.id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50"><RefreshCw className="w-3.5 h-3.5" /></button>
                    )}
                    <button onClick={() => remove(c)} title="Remove" data-testid={`cert-delivery-remove-${c.id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
                  </span>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editCert && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditCert(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-3" onClick={e => e.stopPropagation()} data-testid="cert-delivery-edit-modal">
            <h3 className="font-bold text-slate-800">{editCert._resend ? 'Fix email & resend' : 'Edit recipient'}</h3>
            {editCert._resend && <p className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800">This email bounced ({editCert.email}). Enter the correct address and the certificate will be resent immediately.</p>}
            <p className="text-xs text-slate-400">File: {editCert.filename}</p>
            <label className="text-xs font-semibold text-slate-500">Recipient name (used in the letter)</label>
            <input value={editCert.recipient_name || ''} onChange={e => setEditCert(c => ({ ...c, recipient_name: e.target.value }))}
              data-testid="cert-delivery-edit-name" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <label className="text-xs font-semibold text-slate-500">Email address</label>
            <input value={editCert.email || ''} onChange={e => setEditCert(c => ({ ...c, email: e.target.value }))}
              placeholder="participant@email.com" data-testid="cert-delivery-edit-email" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <div className="flex gap-2 pt-1">
              {editCert._resend ? (
                <button onClick={() => saveEdit(true)} data-testid="cert-delivery-save-resend"
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg inline-flex items-center gap-2">
                  <Send className="w-4 h-4" /> Save &amp; resend
                </button>
              ) : (
                <button onClick={() => saveEdit(false)} data-testid="cert-delivery-edit-save" className="px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg">Save</button>
              )}
              <button onClick={() => setEditCert(null)} data-testid="cert-delivery-edit-cancel" className="px-4 py-2 border border-slate-200 text-slate-500 text-sm font-semibold rounded-lg">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="cert-delivery-preview-modal">
            <div className="px-5 py-3 flex items-center justify-between border-b bg-slate-50 rounded-t-xl sticky top-0">
              <div className="text-xs">
                <p className="font-bold text-slate-700">To: {preview.to || 'No email yet'} · Attachment: {preview.attachment}</p>
                <p className="text-slate-500">Subject: {preview.subject}</p>
              </div>
              <button onClick={() => setPreview(null)} data-testid="cert-delivery-preview-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <iframe title="Email preview" srcDoc={preview.html} className="w-full" style={{ height: '60vh', border: 'none' }} />
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              {preview.cert.status !== 'sent' && preview.cert.email && (
                <button onClick={() => { sendOne(preview.cert); setPreview(null); }} data-testid="cert-delivery-preview-send" className="px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg inline-flex items-center gap-2">
                  <Send className="w-4 h-4" /> Send this one
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertDeliveryView;
